import React from 'react';
import { Icon } from '@iconify/react';
import { Typography } from '@heroui/react';
import { Stepper } from '@heroui-pro/react';
import { useTranslation } from 'react-i18next';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    buildStepStrip,
    stepPosition,
    type OnboardingStep,
    type StepStripItem,
    type StepStripLinks,
    type StepStripMode,
} from './stepStripMeta';
import { StepTitle } from './StepTitle';

/**
 * A skipped step sits before the current one, so the `Stepper` would colour
 * it complete and read it as done. The Stepper's documented colour variables,
 * pointed at neutral tokens, keep it muted instead.
 */
const SKIPPED_STEP_COLORS = {
    '--stepper-complete-color': 'var(--color-default)',
    '--stepper-complete-fg': 'var(--color-muted)',
} as React.CSSProperties;

type StepStripProps = {
    mode: StepStripMode;
    currentStep: OnboardingStep | null;
    accountingNeeded: boolean;
    /**
     * The steps the owner may go back to, each to its address
     * (`linkableSteps`). Absent, no step is a link.
     */
    links?: StepStripLinks;
};

/**
 * The wizard's progress strip, shared with the in-shell import page in
 * `shell` mode (File · Names · Products · Result).
 *
 * The steps in `links` are ways back: their titles are HeroUI `Link`s to
 * the step's address, named "Back to <step>". Every other step behind the
 * current one (done or skipped) is display only, its title muted and out
 * of the Tab order. The HeroUI Pro `Stepper` itself stays non-interactive:
 * its `onStepChange` would make every step a button, steps ahead included.
 *
 * From 640 px it is the full `Stepper`; below that it collapses to the
 * current step's name and "n of m", which links nowhere. Both are in the
 * DOM and CSS picks one, so the switch needs no resize listener.
 */
export const StepStrip: React.FC<StepStripProps> = ({
    mode,
    currentStep,
    accountingNeeded,
    links,
}) => {
    const { t } = useTranslation('pages');
    const items = React.useMemo(
        () => buildStepStrip({ mode, currentStep, accountingNeeded }),
        [mode, currentStep, accountingNeeded],
    );
    const position = stepPosition(items);
    const currentIndex = items.findIndex((item) => item.state === 'current');

    return (
        <nav
            aria-label={t(`onboarding.frame.stripLabel.${mode}`)}
            {...getNodeTestIdProp(`onboarding-step-strip-${mode}`)}
        >
            <Stepper
                className="hidden sm:flex"
                currentStep={currentIndex}
                size="sm"
                {...getNodeTestIdProp('onboarding-step-strip-full')}
            >
                {items.map((item) => (
                    <Stepper.Step
                        aria-current={
                            item.state === 'current' ? 'step' : undefined
                        }
                        data-state={item.state}
                        key={item.step}
                        style={
                            item.state === 'skipped'
                                ? SKIPPED_STEP_COLORS
                                : undefined
                        }
                        {...getNodeTestIdProp(
                            `onboarding-step-strip-step-${item.step}`,
                        )}
                    >
                        <StepIndicator item={item} />
                        <Stepper.Content>
                            <StepTitle href={links?.[item.step]} item={item} />
                            {item.isOptional ? (
                                <Stepper.Description>
                                    {item.state === 'skipped'
                                        ? t('onboarding.frame.skipped')
                                        : t('onboarding.frame.optional')}
                                </Stepper.Description>
                            ) : null}
                        </Stepper.Content>
                        <Stepper.Separator />
                    </Stepper.Step>
                ))}
            </Stepper>
            {position && currentStep ? (
                <div
                    className="flex items-baseline justify-between gap-3 sm:hidden"
                    {...getNodeTestIdProp('onboarding-step-strip-compact')}
                >
                    <Typography type="body-sm" weight="semibold">
                        {t(`onboarding.steps.${currentStep}`)}
                    </Typography>
                    <Typography color="muted" type="body-xs">
                        {t('onboarding.frame.position', {
                            position: position.position,
                            total: position.total,
                        })}
                    </Typography>
                </div>
            ) : null}
        </nav>
    );
};

/** A skipped step shows a dash instead of its number or a checkmark. */
const StepIndicator: React.FC<{ item: StepStripItem }> = ({ item }) =>
    item.state === 'skipped' ? (
        <Stepper.Indicator>
            <Stepper.Icon>
                <Icon icon="solar:minus-circle-linear" />
            </Stepper.Icon>
        </Stepper.Indicator>
    ) : (
        <Stepper.Indicator />
    );
